// ============================================================================
// Barber Invitation Types
// ============================================================================

/**
 * Status of a barber invitation (matches backend enum)
 */
export enum BarberInvitationStatus {
  /** Invitation sent, awaiting response */
  Pending = 'Pending',
  /** Invitation accepted by barber */
  Accepted = 'Accepted',
  /** Invitation declined by barber */
  Declined = 'Declined',
  /** Invitation expired (past expiry date) */
  Expired = 'Expired',
  /** Invitation cancelled by shop owner */
  Cancelled = 'Cancelled'
}

/**
 * Barber invitation details
 */
export interface BarberInvitationDto {
  /** Unique identifier */
  id: number;
  /** Platform subscription ID */
  platformSubscriptionId: number;
  /** Barbershop ID */
  barbershopId: number;
  /** Barbershop name (computed) */
  barbershopName: string;
  /** Shop owner user ID */
  shopOwnerUserId: string;
  /** Shop owner name (computed) */
  shopOwnerName?: string;
  /** Invited barber email address */
  email: string;
  /** Invited barber first name (optional) */
  firstName?: string;
  /** Invited barber last name (optional) */
  lastName?: string;
  /** Computed full name from first + last name */
  fullName?: string;
  /** Personal message from shop owner */
  personalMessage?: string;
  /** Role/title offered to barber */
  offeredRole?: string;
  /** Current status of invitation */
  status: BarberInvitationStatus;
  /** Unique invitation token for acceptance */
  invitationToken: string;
  /** When invitation expires */
  expiresAt: string;
  /** When invitation was created */
  createdAt: string;
  /** When invitation was accepted (if applicable) */
  acceptedAt?: string;
  /** When invitation was declined (if applicable) */
  declinedAt?: string;
  /** When invitation was cancelled (if applicable) */
  cancelledAt?: string;
  /** User ID of barber who accepted (if applicable) */
  acceptedByUserId?: string;
  /** Barber profile ID created after acceptance (if applicable) */
  barberProfileId?: number;
  /** Number of times invitation email was sent */
  emailSendCount: number;
  /** When invitation email was last sent */
  lastEmailSentAt?: string;
  /** Reason for declining (if applicable) */
  declineReason?: string;
}

/**
 * Request to create a new barber invitation
 */
export interface CreateBarberInvitationRequest {
  /** Barbershop ID to invite barber to */
  barbershopId: number;
  /** Email address to send invitation to */
  email: string;
  /** First name of invited barber (optional) */
  firstName?: string;
  /** Last name of invited barber (optional) */
  lastName?: string;
  /** Personal message from shop owner (optional) */
  personalMessage?: string;
  /** Role/title offered to barber (optional) */
  offeredRole?: string;
  /** Days until invitation expires (default: 7, max: 30) */
  expiryDays?: number;
}

/**
 * Request to accept an invitation
 */
export interface AcceptInvitationRequest {
  /** Invitation token from URL/email */
  invitationToken: string;
  /** User ID of accepting barber */
  userId: string;
  /** Barber display name (optional, can be updated later) */
  barberName?: string;
  /** Barber tag/username (optional, can be updated later) */
  tagName?: string;
  /** Barber bio (optional, can be updated later) */
  bio?: string;
  /** Barber specialties (optional, can be updated later) */
  specialties?: string;
}

/**
 * Response after accepting an invitation
 */
export interface AcceptInvitationResponse {
  success: boolean;
  message: string;
  /** Created barber profile */
  barberProfile?: {
    id: number;
    userId: string;
    barbershopId: number;
    barberName?: string;
    tagName?: string;
  };
  /** Created seat assignment */
  seatAssignment?: {
    id: number;
    assignmentType: string;
  };
}

/**
 * Request to decline an invitation
 */
export interface DeclineInvitationRequest {
  /** Invitation token from URL/email */
  invitationToken: string;
  /** Optional reason for declining */
  reason?: string;
}

/**
 * Response after declining an invitation
 */
export interface DeclineInvitationResponse {
  success: boolean;
  message: string;
}

/**
 * Request to cancel an invitation
 */
export interface CancelInvitationRequest {
  /** Optional reason for cancellation */
  reason?: string;
}

/**
 * Response after cancelling an invitation
 */
export interface CancelInvitationResponse {
  success: boolean;
  message: string;
}

/**
 * Response after resending an invitation
 */
export interface ResendInvitationResponse {
  success: boolean;
  message: string;
  /** Number of times email has been sent */
  emailSendCount: number;
  /** When email was last sent */
  lastEmailSentAt: string;
}

/**
 * Invitation token validation response
 */
export interface ValidateTokenResponse {
  isValid: boolean;
  message: string;
  /** Invitation details if valid */
  invitation?: BarberInvitationDto;
}

/**
 * Statistics for invitation management
 */
export interface InvitationStatisticsDto {
  /** Total invitations sent */
  totalInvitations: number;
  /** Number of pending invitations */
  pendingCount: number;
  /** Number of accepted invitations */
  acceptedCount: number;
  /** Number of declined invitations */
  declinedCount: number;
  /** Number of expired invitations */
  expiredCount: number;
  /** Number of cancelled invitations */
  cancelledCount: number;
  /** Acceptance rate (accepted / total) */
  acceptanceRate: number;
  /** Average days to acceptance */
  averageDaysToAcceptance?: number;
}
